/**
 * Suggestions de rencontres (section 5) — le cœur différenciant du produit.
 *
 * Chaque carte dit POURQUOI elle est là : l'explication est affichée en
 * premier, avant même le nom. Refuser une suggestion la retire définitivement
 * (signal comportemental) ; ouvrir la fiche la marque comme consultée.
 */
import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { appelerApi, envoyer } from '../reseau.ts';
import { useSession } from '../etat.tsx';
import { Avatar, nomAffiche } from '../composants/FicheParticipant.tsx';

type Suggestion = {
  id: string;
  suggere_id: string;
  score: string;
  explication: string;
  fiche: {
    participant_id: string;
    prenom: string | null;
    nom: string | null;
    entreprise: string | null;
    fonction: string | null;
    secteur: string | null;
    besoins: string | null;
    present: boolean;
  };
};

export function Suggestions() {
  const { moi } = useSession();
  const naviguer = useNavigate();
  const [suggestions, setSuggestions] = useState<Suggestion[] | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);

  const charger = async () => {
    const r = await appelerApi<{ suggestions: Suggestion[] }>('/api/matchmaking/suggestions');
    if (r.donnees) setSuggestions(r.donnees.suggestions);
    else setErreur(r.erreur);
  };

  useEffect(() => {
    void charger();
  }, []);

  const ouvrir = async (s: Suggestion) => {
    void envoyer(`/api/matchmaking/suggestions/${s.id}/action`, { action: 'consultee' });
    naviguer(`/participants/${s.suggere_id}`);
  };

  const refuser = async (s: Suggestion) => {
    setSuggestions((liste) => (liste ?? []).filter((x) => x.id !== s.id));
    await envoyer(`/api/matchmaking/suggestions/${s.id}/action`, { action: 'refusee' });
  };

  if (erreur) {
    return (
      <div className="suggestions">
        <h1>Personnes à rencontrer</h1>
        <div className="carte suggestions__incomplet">
          <p>{erreur}</p>
          {!moi?.profil_complete && (
            <Link to="/profil" className="bouton">
              Compléter mon profil
            </Link>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="suggestions">
      <h1>Personnes à rencontrer</h1>
      <p className="suggestions__intro">
        Sélectionnées pour vous d'après vos besoins et vos objectifs — et mises à jour au fil de
        l'événement.
      </p>

      {suggestions === null ? (
        <p>Calcul en cours…</p>
      ) : suggestions.length === 0 ? (
        <p className="suggestions__vide">
          Les suggestions arrivent dès que suffisamment de profils sont complétés — revenez un peu
          plus tard.
        </p>
      ) : (
        <ul className="suggestions__liste">
          {suggestions.map((s) => (
            <li key={s.id} className="carte suggestion">
              <p className="suggestion__explication">{s.explication}</p>
              <button className="suggestion__personne" onClick={() => void ouvrir(s)}>
                <span className="ligne-participant__avatar">
                  <Avatar p={{ ...s.fiche, participant_id: s.suggere_id }} />
                  {s.fiche.present && <span className="pastille-presence" title="Sur place" />}
                </span>
                <span className="ligne-participant__texte">
                  <span className="ligne-participant__nom">{nomAffiche(s.fiche)}</span>
                  <span className="ligne-participant__detail">
                    {[s.fiche.fonction, s.fiche.entreprise].filter(Boolean).join(' · ')}
                  </span>
                </span>
              </button>
              <div className="suggestion__actions">
                <button className="bouton" onClick={() => void ouvrir(s)}>
                  Voir et proposer un rendez-vous
                </button>
                <button
                  className="bouton bouton--secondaire"
                  onClick={() => void refuser(s)}
                  aria-label={`Écarter la suggestion ${nomAffiche(s.fiche)}`}
                >
                  Pas pertinent
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
